import { clsx } from "clsx";
import { type ScaleLinear } from "d3";
import { type FC } from "react";
import { useHover } from "react-aria";

import { TriggerButton, TriggerButtonGroup, TriggerButtonSize } from "widgets";

import styleTrack from "../styles/Track.module.scss";

import { conditionallyPluralizeTrackLabel } from "../../../app-utils/string.js";
import { type Track, type TrackCoordinateOrderedCoordinateList } from "../../../graphql.js";
import { type SetTooltipContent, ShowMessageOverTrack } from "../GenomeViewerTracksList.js";
import { TrackHeader } from "../TrackHeader.js";
import { CoordinateTrackLoader } from "./CoordinateTrackLoader.js";

interface ICoordinateTrackProps {
  track: Track;
  scale: ScaleLinear<number, number>;
  handleCustomize: () => void;
  setTooltipContent: SetTooltipContent;
}

const CoordinatesTrack: FC<ICoordinateTrackProps> = ({ track, ...props }) => {
  const { hoverProps, isHovered } = useHover({});
  const trackMessage = "Select Coordinate Lists to display here";
  const emptyTrack = !track.trackCustomization?.coordinateConfiguration?.orderedCoordinateLists?.length;

  if (emptyTrack) {
    return (
      <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
        <TrackHeader
          isTrackHovered={isHovered}
          title={conditionallyPluralizeTrackLabel(track)}
          trackIcons={
            <TriggerButtonGroup vertical>
              <TriggerButton
                icon={"settings"}
                label={trackMessage}
                onPress={() => props.handleCustomize()}
                size={TriggerButtonSize.Small}
              />
            </TriggerButtonGroup>
          }
        />
        <div className={styleTrack.container}>
          <ShowMessageOverTrack message={trackMessage} onClick={props.handleCustomize} />
        </div>
      </div>
    );
  }

  return (
    <>
      {track.trackCustomization.coordinateConfiguration.orderedCoordinateLists.map(
        (coordinateList: TrackCoordinateOrderedCoordinateList, index) => {
          return (
            <CoordinateTrackLoader
              track={track}
              scale={props.scale}
              coordinateListData={coordinateList}
              key={`coordinate-track-${coordinateList.coordinateListId}-${index}`}
              setTooltipContent={props.setTooltipContent}
            />
          );
        },
      )}
    </>
  );
};

export { CoordinatesTrack };
